import React from "react";
import PropTypes from "prop-types";

import { DataGrid, Toolbar, QuickFilter, QuickFilterControl, QuickFilterClear } from "@mui/x-data-grid";
import { Box, Button, IconButton, InputAdornment, TextField } from "@mui/material";
import {
  Edit,
  SearchIcon as Search,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "components/icons";
import MDBox from "components/MDBox";
import { isEmpty } from "utils/validationUtil";
import { DEFAULT_TABLE_PAGE_SIZE } from "AppConstants";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// @since NEXT_VERSION
export const DepartmentsGridToolbar = ({ onAddDepartment }) => (
  <Toolbar>
    <Box sx={{ py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, width: "100%" }}>
      {onAddDepartment && (
        <Button
          startIcon={<AddIcon />}
          onClick={onAddDepartment}
          sx={{ width: 190 }}
          variant="contained"
          color="success"
        >
          Add Department
        </Button>
      )}
      <QuickFilter expanded debounceMs={300}>
        <QuickFilterControl
          render={(controlProps) => (
            <TextField
              {...controlProps}
              size="small"
              placeholder="Search..."
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "transparent",
                  height: 36.5,
                  fontSize: "1rem",
                },
                "& .MuiInputBase-input": { fontSize: "1rem" },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
        <QuickFilterClear />
      </QuickFilter>
    </Box>
  </Toolbar>
);

DepartmentsGridToolbar.propTypes = {
  onAddDepartment: PropTypes.func,
};

// @since NEXT_VERSION
export const DepartmentsDataGrid = (props) => {
  const { departments, isLoading, onAddDepartment, editEventHandler, confirmDeleteDialogHandler } = props;

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      cellClassName: "actions",
      getActions: (obj) => [
        <IconButton aria-label="Edit" onClick={() => editEventHandler(obj.row)} color="inherit">
          <Edit />
        </IconButton>,
        <IconButton
          aria-label="Disable"
          onClick={() => confirmDeleteDialogHandler(obj.row)}
          color="inherit"
        >
          <DeleteIcon />
        </IconButton>,
      ],
    },
    { field: "id", headerName: "DeptId", width: 100 },
    { field: "code", headerName: "DeptCode", width: 140 },
    { field: "displayName", headerName: "Display Name", width: 220 },
    { field: "description", headerName: "Description", width: 320 },
    { field: "status", headerName: "Status", width: 140 },
  ];

  const getData = () => (isEmpty(departments) ? [] : departments);

  return (
    <MDBox py={1.5} px={2} height={500}>
      <DataGrid
        density="compact"
        rows={getData()}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialState={{
          // DeptId is auto-generated and rarely useful at a glance; available via the column menu.
          columns: { columnVisibilityModel: { id: false } },
          pagination: { paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE } },
          sorting: { sortModel: [{ field: "displayName", sort: "asc" }] },
        }}
        showToolbar
        slots={{ toolbar: DepartmentsGridToolbar }}
        slotProps={{ toolbar: { onAddDepartment } }}
        disableRowSelectionOnClick
      />
    </MDBox>
  );
};

DepartmentsDataGrid.propTypes = {
  departments: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onAddDepartment: PropTypes.func,
  editEventHandler: PropTypes.func.isRequired,
  confirmDeleteDialogHandler: PropTypes.func.isRequired,
};
